import { ChangeDetectionStrategy, ChangeDetectorRef, Component, OnInit, ViewEncapsulation } from '@angular/core';
import { AbstractControl, UntypedFormBuilder, UntypedFormGroup, ValidationErrors, Validators } from '@angular/forms';
import { Observable, of } from 'rxjs';
import { GraphqlService } from 'src/app/_core/services/graphql.service';
import { CustomFormControlAccess } from 'src/app/_core/tools/custom-form-control-access';
import { buildProviders } from '@kalidea/kaligraphi';
import { FormValidator } from 'src/app/validators/form-validator';
import { ArrayOfstring, MutationGetListeBanquesFromMouvementComptableArgs } from 'src/graphql/generated-types';
import gql from 'graphql-tag';
import { tap } from 'rxjs/operators';
import { wheneverEnd } from 'src/app/utils/operators.utils';

export interface ChequeEditType {
    chequeNumber: string;
    bankName: string;
    tireName: string;
}

export const MAX_LENGTH_CHEQUE_NUMBER = 20;
const MAX_LENGTH_BANK_NAME = 25;
const MAX_LENGTH_TIRE_NAME = 50;

@Component({
    selector: 'app-shared-cheque-edit',
    templateUrl: './shared-cheque-edit.component.html',
    styleUrls: ['./shared-cheque-edit.component.sass'],
    encapsulation: ViewEncapsulation.None,
    changeDetection: ChangeDetectionStrategy.OnPush,
    providers: buildProviders(SharedChequeEditComponent),
    standalone: false
})
export class SharedChequeEditComponent extends CustomFormControlAccess implements OnInit {

    banksList: string[] = [];

    form: UntypedFormGroup;

    constructor(private readonly cdr: ChangeDetectorRef,
                private readonly fb: UntypedFormBuilder,
                private readonly graphqlService: GraphqlService) {
        super();
    }

    private _loading = false;

    get loading(): boolean {
        return this._loading;
    }

    set loading(value: boolean) {
        this._loading = value;
        this.cdr.markForCheck();
    }

    validate(c: AbstractControl): Observable<ValidationErrors | null> {
        return of(this.form.valid ? null : {chequeediterror: true});
    }

    writeValue(value: ChequeEditType): void {
        this.updateForm(value);
        super.writeValue(value);
    }

    private updateForm(value: ChequeEditType): void {
        this.form.patchValue({
            chequeNumber: value?.chequeNumber || null,
            bankName: value?.bankName || null,
            tireName: value?.tireName || null
        }, {emitEvent: false});
    }

    private loadList$(): Observable<ArrayOfstring> {
        return this.graphqlService.mutateOne<'GetListeBanquesFromMouvementComptable', MutationGetListeBanquesFromMouvementComptableArgs>({
            mutation: gql`
                mutation GetListeBanquesFromMouvementComptable( $contexte: ContextInfosInput ) {
                    GetListeBanquesFromMouvementComptable( contexte: $contexte ){
                        string
                    }
                }`
        }).pipe(
            tap(GetListeBanquesFromMouvementComptable => {
                this.banksList = GetListeBanquesFromMouvementComptable?.string || [];
            })
        );
    }

    private initSubscription(): void {
        this.watch$ = this.form.valueChanges.pipe(
            tap(value => this.notifyUpdate({
                ...this.form.getRawValue(),
                ...value
            }))
        );

    }

    ngOnInit(): void {
        this.form = this.fb.group({
            chequeNumber: [null, [Validators.maxLength(MAX_LENGTH_CHEQUE_NUMBER), FormValidator.isNotNull, FormValidator.number]],
            bankName: [null, [Validators.maxLength(MAX_LENGTH_BANK_NAME)]],
            tireName: [null, [Validators.maxLength(MAX_LENGTH_TIRE_NAME)]],
        });

        this.initSubscription();

        this.loading = true;
        this.watch$ = this.loadList$().pipe(
            wheneverEnd(() => this.loading = false)
        );
    }
}
